import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Heading,
  Button,
  TextField,
  ButtonGroup
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import {
  NavHeader,
  Loader,
  getTranslatedText,
  TableContainer,
  ContentDeciderContainer,
  SkeletonTableLoading,
  EventEmitter,
  Tag,
  DATE_TIME_FORMAT,
  useActivityTracker,
  CustomModal,
  AccessControl,
  useCheckFeatureGating
} from '@nile/components';
import { NavLink } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import envConfig from '@nile/home/src/Config/env';
import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import WorklogContext, {
  WorklogContextProvider
} from '@nile/client_management/src/Contexts/WorklogContext';
import {
  GET_SERVICE_CATEGORIES,
  AccountClient,
  useQuery,
  GET_WHITELISTED_SERVICE_ITEMS,
  CONSTRUCT_INVOICE_ITEMS,
  MockTicketClient,
  ADD_WORKLOG_ENTRIES
} from '@nile/graphql';
import { cloneDeep, debounce } from 'lodash';
import nanoId from 'nanoid';
import dayjs from 'dayjs';
import { Tooltip } from 'react-clone-tippy';
import NewServiceCatalog from '@nile/banyan/src/Pages/ServiceCatalog/NewServiceCatalog';
import ItemsToAddTable from './ItemsToAddTable';
import { OFFERING_TYPES } from '@nile/banyan/src/Pages/Constants/offeringType';
import { useFlags } from 'launchdarkly-react-client-sdk';

function ItemsToInvoice(props) {
  const { intl, closeModal, ticket, project, module } = props;
  const { loading, data, error } = useQuery(GET_SERVICE_CATEGORIES, {
    client: AccountClient,
    fetchPolicy: 'network-only'
  });
  const flags = useFlags();
  const [selectedSection, setSelectedSection] = useState('all');
  const [allServiceItems, setAllServiceItems] = useState(null);
  const [filterText, setFilterText] = useState(null);
  const [records, setRecords] = useState({});
  const [listInfo, setListInfo] = useState({});
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);

  const contractsNew = useCheckFeatureGating('CONTRACT_V3')?.enabled;

  const [showNewServiceItemSheet, setShowNewServiceItemSheet] = useState();

  const setSelectedRecords = (recordsInput, overrideSelectedSection) => {
    const allRecordsSet = [...new Set(recordsInput)];
    const selectedSectionItem =
      overrideSelectedSection || selectedSection || 'all';
    setRecords({
      ...records,
      [selectedSectionItem]: allRecordsSet
    });
  };

  const setRecordsForMultipleSections = (properties) => {
    setRecords({
      ...records,
      ...properties
    });
  };

  const [
    createWorklogEntry,
    {
      loading: createWorklogLoading,
      data: createdWorklog,
      error: createWorklogError
    }
  ] = useMutation(ADD_WORKLOG_ENTRIES, {
    onCompleted() {
      closeModal();
      useActivityTracker({
        event: 'Billing - products added'
      });
      EventEmitter.dispatch('refetchProductWorklogs');
    }
  });
  useEffect(() => {
    // getContainerData(selectedSection);
  }, [selectedSection]);
  const fetchFilteredData = (text) => {
    EventEmitter.dispatch('filterData', text);
  };

  const debouncedFilter = useCallback(debounce(fetchFilteredData, 1000), []);
  if (loading) return <Loader />;
  const allCategories = data.getServiceCategories
    ? data.getServiceCategories.slice()
    : [];
  // allCategories.unshift({
  //   name: 'all',
  //   label: getTranslatedText(intl, 'invoiceBuilder.itemSelection.allCategories')
  // });
  const handleSelection = () => {
    let otherRecords = [];
    Object.keys(records).forEach((el) => {
      otherRecords = otherRecords.concat(records[el]);
    });
    const input = otherRecords.map((el) => {
      return {
        [contractsNew ? 'serviceCatalogItem' : 'serviceItem']: {
          itemId: el?.itemId
        },
        billable: true,
        qty: '1',
        billDateTime: dayjs().format(DATE_TIME_FORMAT),
        notes:
          el?.[contractsNew ? 'description' : 'invoiceDescription'] || el?.name,
        module: module,
        workItem:
          ticket?.ticketId || project?.projectId
            ? {
                workId: ticket?.ticketId || project?.projectId,
                module: module || 'TICKET'
              }
            : null
      };
    });

    createWorklogEntry({
      variables: {
        input: { entries: input }
      }
    });
  };

  const contextValue = {
    records: records,
    setSelectedRecords: setSelectedRecords,
    setRecordsForMultipleSections: setRecordsForMultipleSections,
    selectedSection: selectedSection
  };
  let allSelectedItems = [];
  Object.keys(records).forEach((el) => {
    allSelectedItems = allSelectedItems.concat(records[el] || []);
  });
  const checkRowHighlight = (item) => {
    return (
      allSelectedItems?.findIndex(
        (el) => el.itemId === item?.original?.itemId
      ) > -1
    );
  };
  const checkDisabledRow = (item) => {
    return false;
  };
  return (
    <div className="w1200 vh90 bg-white rounded-2xl">
      <WorklogContextProvider value={contextValue}>
        <div className="p2040 fx actr">
          <Heading>
            <FormattedMessage id="invoiceBuilder.itemSelection.modal.header" />
          </Heading>
          <div className="w200 ml-20">
            <TextField
              value={filterText}
              placeholder={getTranslatedText(
                intl,
                'invoiceBuilder.catalogItems.search.placeholder'
              )}
              onChange={(val) => {
                setFilterText(val);
                // setting this to null as it's just a gimmick to focus all categories. Else API call will go for all instead of filtered text.
                setSelectedSection(null);
                debouncedFilter(val);
              }}
              autoComplete={false}
              prefix={<SVGIcon name="Search" />}
              suffix={
                filterText ? (
                  <div
                    className="position-relative z21"
                    onClick={() => {
                      setFilterText(null);
                      debouncedFilter('');
                    }}
                  >
                    <SVGIcon name="CircleCancelMinor" />
                  </div>
                ) : null
              }
              size="small"
              variant="outline"
            />
          </div>
          <div className="mlauto flex center g10">
            {allSelectedItems?.length > 0 && (
              <Tooltip
                distance={5}
                html={
                  <div className="scroll w300 br10 p15 max-height-500 bg-white bshadow ">
                    {allSelectedItems.map((el) => {
                      return (
                        <div className="flex center py8 fsize12 white-space-wrap">
                          <div className="self-start">
                            <SVGIcon
                              name={
                                OFFERING_TYPES[
                                  el.serviceCatalogItem?.serviceTypeItem
                                    ?.offeringType || 'PRODUCT'
                                ].icon
                              }
                            />
                          </div>
                          <div className="ml8 self-start">
                            {el.name || el.serviceCatalogItem?.name}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                }
                position="bottom-end"
                inertia
                trigger="mouseenter"
                animation="shift"
                interactive
                animateFill={false}
                theme="super-light"
              >
                <Tag className="pointer border-black" background="#FFFEF0">
                  {`${allSelectedItems.length} ${getTranslatedText(
                    intl,
                    'admin.serviceCatalog.list.selected'
                  )}`}
                </Tag>
              </Tooltip>
            )}
            <ButtonGroup>
              {props.locked && props.closeModal && (
                <Button plainSecondary onClick={props.closeModal}>
                  <FormattedMessage id="common.cancel" />
                </Button>
              )}

              <AccessControl
                roleToCheck="serviceCatalog.add"
                render={
                  <Button
                    icon={<SVGIcon name="Add" />}
                    onClick={() => {
                      setShowNewServiceItemSheet(true);
                    }}
                  >
                    <FormattedMessage id="client.contractTemplate.form.createItem" />
                  </Button>
                }
              />

              <Button
                primary
                icon={<SVGIcon name="LikeThumb" />}
                onClick={() => {
                  handleSelection();
                }}
                loading={createWorklogLoading}
              >
                <FormattedMessage id="app.action.done" />
              </Button>
            </ButtonGroup>
          </div>
        </div>
        <div className="fx actr ch-90">
          <div className="fhp scroll w300 p1025 border-right border-top">
            <div className="fx py-2 pt10 pb10 mt10 mb8 border-bottom f-12 bold">
              <NavHeader>
                <FormattedMessage id="invoiceBuilder.itemSelection.modal.serviceCategories" />
              </NavHeader>
            </div>
            <div className="LinkGroup mb40">
              <div
                className={`fx actr ticketing-actions p10 br-5 ptr ${
                  selectedSection === 'all' || !selectedSection
                    ? 'lightpurple-to-black-bg font-weight-600'
                    : 'bg-none'
                }`}
                strict
                key="category-all"
                replace
                onClick={() => {
                  setSelectedSection('all');
                  setFilterText(null);
                }}
              >
                <FormattedMessage id="invoiceBuilder.itemSelection.allCategories" />
              </div>
              {allCategories.map((el) => {
                return (
                  <div
                    className={`fx actr ticketing-actions p10 br-5 ptr ${
                      el.name === selectedSection
                        ? 'lightpurple-to-black-bg font-weight-600'
                        : 'bg-none'
                    }`}
                    strict
                    key={`category-${el.name}`}
                    replace
                    onClick={() => {
                      setSelectedSection(el.name);
                      setFilterText(null);
                    }}
                  >
                    {el.label || el.name}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="fhp cw-300">
            <ItemsToAddTable
              selectedSection={selectedSection}
              client={props.client}
              setRecords={setSelectedRecords}
              loading={createWorklogLoading}
              checkDisabledRow={checkDisabledRow}
              checkRowHighlight={checkRowHighlight}
              renderServiceItemSheet={(fetchTableViewData) => {
                return (
                  <CustomModal open={showNewServiceItemSheet}>
                    <div className="vh90">
                      <NewServiceCatalog
                        onBack={() => setShowNewServiceItemSheet(false)}
                        onServiceItemCreation={async (serviceItem) => {
                          await fetchTableViewData();
                          const selectedSectionItem =
                            serviceItem.category?.name || 'all';
                          const newRecords =
                            cloneDeep(records[selectedSectionItem]) || [];
                          newRecords.push(serviceItem);
                          setSelectedSection(selectedSectionItem);
                          setSelectedRecords(newRecords, selectedSectionItem);
                          setShowNewServiceItemSheet(false);
                        }}
                        removePadding
                        fullWidth={false}
                      />
                    </div>
                  </CustomModal>
                );
              }}
            />
          </div>
        </div>
      </WorklogContextProvider>
    </div>
  );
}

export default injectIntl(ItemsToInvoice);
